import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Label } from '../../ui/label'
import { Badge } from '../../ui/badge'
import { toast } from 'sonner'
import { facturamaService } from '../../../services/facturama.service'
import { salesService, Sale } from '../../../services/sales.service'
import { banksService, BankItem } from '../../../services/banks.service'
import { paymentMethodService, PaymentMethodItem } from '../../../services/payment-method.service'
import { formatShortTicket } from '../../../lib/utils'
import { CatalogSelect } from '../../ui/CatalogSelect'
import { useQueryClient } from '@tanstack/react-query'
import {
  FileText,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Search,
  CheckSquare,
  Square,
  AlertCircle,
  Receipt,
  Calendar,
  Building2,
  Info,
  ArrowRight,
  CheckCircle,
  Download,
} from 'lucide-react'
import { cn } from '../../../lib/utils'

// RFC de Público en General según SAT
const RFC_GENERAL_PUBLIC = 'XAXX010101000'

interface GlobalInvoiceWizardProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

type Step = 'period' | 'review' | 'confirm' | 'success'

const STEPS: { key: Step; label: string; number: number }[] = [
  { key: 'period', label: 'Período', number: 1 },
  { key: 'review', label: 'Revisión', number: 2 },
  { key: 'confirm', label: 'Confirmación', number: 3 },
]

const PERIODICITY_OPTIONS = [
  { value: '01', label: 'Diaria' },
  { value: '02', label: 'Semanal' },
  { value: '03', label: 'Quincenal' },
  { value: '04', label: 'Mensual' },
  { value: '05', label: 'Bimestral' },
]

const MONTH_OPTIONS = [
  { value: '01', label: 'Enero', bimestral: false },
  { value: '02', label: 'Febrero', bimestral: false },
  { value: '03', label: 'Marzo', bimestral: false },
  { value: '04', label: 'Abril', bimestral: false },
  { value: '05', label: 'Mayo', bimestral: false },
  { value: '06', label: 'Junio', bimestral: false },
  { value: '07', label: 'Julio', bimestral: false },
  { value: '08', label: 'Agosto', bimestral: false },
  { value: '09', label: 'Septiembre', bimestral: false },
  { value: '10', label: 'Octubre', bimestral: false },
  { value: '11', label: 'Noviembre', bimestral: false },
  { value: '12', label: 'Diciembre', bimestral: false },
  { value: '13', label: 'Enero–Febrero', bimestral: true },
  { value: '14', label: 'Marzo–Abril', bimestral: true },
  { value: '15', label: 'Mayo–Junio', bimestral: true },
  { value: '16', label: 'Julio–Agosto', bimestral: true },
  { value: '17', label: 'Septiembre–Octubre', bimestral: true },
  { value: '18', label: 'Noviembre–Diciembre', bimestral: true },
]

// ------ Lista de AÑOS dinámica ------
const CURRENT_YEAR = new Date().getFullYear()
// Genera años desde 2023 hasta el año actual + 1
const AVAILABLE_YEARS = Array.from({ length: CURRENT_YEAR - 2023 + 2 }, (_, i) => 2023 + i)

// Formatea una fecha YYYY-MM-DD a texto legible en español
function formatDateLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const months = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ]
  return `${d} de ${months[m - 1]} de ${y}`
}

function getDateRangeForPeriod(
  periodicity: string,
  month: string,
  year: string,
  dailyDate: string,
  week: string,
  quincena: string
): { dateFrom: string; dateTo: string } {
  const y = parseInt(year, 10)
  const m = parseInt(month, 10)

  if (periodicity === '01') {
    return { dateFrom: dailyDate, dateTo: dailyDate }
  }

  if (periodicity === '02') {
    const w = parseInt(week, 10)
    const startDay = (w - 1) * 7 + 1
    const lastDay = new Date(y, m, 0).getDate()
    const endDay = w === 5 ? lastDay : w * 7
    const actualEndDay = Math.min(endDay, lastDay)

    if (startDay > lastDay) {
      throw new Error('Semana no válida')
    }
    const dateFrom = `${year}-${String(m).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`
    const dateTo = `${year}-${String(m).padStart(2, '0')}-${String(actualEndDay).padStart(2, '0')}`
    return { dateFrom, dateTo }
  }

  if (periodicity === '03') {
    if (quincena === '1') {
      const dateFrom = `${year}-${String(m).padStart(2, '0')}-01`
      const dateTo = `${year}-${String(m).padStart(2, '0')}-15`
      return { dateFrom, dateTo }
    } else {
      const dateFrom = `${year}-${String(m).padStart(2, '0')}-16`
      const lastDay = new Date(y, m, 0).getDate()
      const dateTo = `${year}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
      return { dateFrom, dateTo }
    }
  }

  // Meses bimestrales
  if (m >= 13) {
    const firstMonth = (m - 13) * 2 + 1
    const lastMonth = firstMonth + 1
    const dateFrom = `${year}-${String(firstMonth).padStart(2, '0')}-01`
    const lastDay = new Date(y, lastMonth, 0).getDate()
    const dateTo = `${year}-${String(lastMonth).padStart(2, '0')}-${lastDay}`
    return { dateFrom, dateTo }
  }

  const dateFrom = `${year}-${String(m).padStart(2, '0')}-01`
  const lastDay = new Date(y, m, 0).getDate()
  const dateTo = `${year}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
  return { dateFrom, dateTo }
}

function isGeneralPublic(sale: Sale): boolean {
  const rfc = (sale as any).customer_data?.rfc
  const customerId = (sale as any).customer_data?.id
  // Público en General si no tiene customer registrado o su RFC es XAXX010101000
  return !customerId || rfc === RFC_GENERAL_PUBLIC || !rfc
}

export function GlobalInvoiceWizard({ isOpen, onClose, onSuccess }: GlobalInvoiceWizardProps) {
  const queryClient = useQueryClient()

  const currentDate = new Date()
  const [step, setStep] = useState<Step>('period')

  // Paso 1
  const [periodicity, setPeriodicity] = useState('04') // Mensual por default
  const [month, setMonth] = useState(String(currentDate.getMonth() + 1).padStart(2, '0'))
  const [year, setYear] = useState(String(currentDate.getFullYear()))
  const [dailyDate, setDailyDate] = useState(currentDate.toISOString().split('T')[0])
  const [week, setWeek] = useState('1')
  const [quincena, setQuincena] = useState('1')
  const [sucursalId, setSucursalId] = useState('')
  const [bankName, setBankName] = useState<string>('all')
  const [paymentMethod, setPaymentMethod] = useState<string>('all')
  const [saleType, setSaleType] = useState<'all' | 'recharge' | 'service' | 'common'>('all')
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodItem[]>([])
  const [banks, setBanks] = useState<BankItem[]>([])

  // Load banks on mount
  useEffect(() => {
    banksService.getItems({ all: true, is_active: true }).then(res => setBanks(res.data || res || []))
    paymentMethodService.getItems({ all: true, is_active: true }).then(res => setPaymentMethods(res.data || res || []))
  }, [])

  // Errores de validación del Paso 1
  const [errors, setErrors] = useState<{ month?: string; year?: string; dailyDate?: string }>({})

  // Paso 2
  const [isLoadingSales, setIsLoadingSales] = useState(false)
  const [eligibleSales, setEligibleSales] = useState<Sale[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set())

  // Paso 3
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Paso 4
  const [generatedCfdiId, setGeneratedCfdiId] = useState<string | null>(null)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [downloadingXml, setDownloadingXml] = useState(false)

  // Resetear al abrir
  useEffect(() => {
    if (isOpen) {
      setStep('period')
      setPeriodicity('04')
      setMonth(String(new Date().getMonth() + 1).padStart(2, '0'))
      setYear(String(new Date().getFullYear()))
      setDailyDate(new Date().toISOString().split('T')[0])
      setWeek('1')
      setQuincena('1')
      setSucursalId('')
      setSaleType('all')
      setEligibleSales([])
      setSelectedIds(new Set())
      setErrors({})
      setGeneratedCfdiId(null)
    }
  }, [isOpen])

  // Se reinicia el mes cuando cambia de bimestral a no bimestral (Y VICEVERSA)
  useEffect(() => {
    const isBimestral = periodicity === '05'
    const currentMonthNum = parseInt(month, 10)
    const monthIsBimestral = currentMonthNum >= 13

    if (isBimestral && !monthIsBimestral) {
      // Se necesita un mes bimestral — escoge el que coincida con el mes calendario actual
      const calMonth = new Date().getMonth() + 1 // 1-12
      // Se mapea a bimestral: 1-2→13, 3-4→14, 5-6→15, 7-8→16, 9-10→17, 11-12→18
      const bimestralMonth = String(13 + Math.floor((calMonth - 1) / 2)).padStart(2, '0')
      setMonth(bimestralMonth)
    } else if (!isBimestral && monthIsBimestral) {
      // Se necesita un mes calendario normal — escoge el mes calendario actual
      setMonth(String(new Date().getMonth() + 1).padStart(2, '0'))
    }
    // Se limpia el error del mes al cambiar
    setErrors((prev) => ({ ...prev, month: undefined }))
  }, [periodicity])

  // Se muestra la fecha de inicio y fin del periodo seleccionado (reactivo)
  const dateRange = useMemo(() => {
    if (periodicity === '01' && !dailyDate) return null
    if (periodicity !== '01' && (!month || !year)) return null
    try {
      return getDateRangeForPeriod(periodicity, month, year, dailyDate, week, quincena)
    } catch {
      return null
    }
  }, [periodicity, month, year, dailyDate, week, quincena])

  // Se valida que los campos del paso 1 sean correctos
  const validateStep1 = (): boolean => {
    const newErrors: { month?: string; year?: string; dailyDate?: string } = {}

    if (periodicity === '01') {
      if (!dailyDate) newErrors.dailyDate = 'Selecciona una fecha'
    } else {
      if (!month) newErrors.month = 'Selecciona un mes o bimestre'
      if (!year) newErrors.year = 'Selecciona un año'

      // Se valida que el mes sea compatible con la periodicidad
      const isBimestral = periodicity === '05'
      const monthNum = parseInt(month, 10)
      if (isBimestral && monthNum < 13) {
        newErrors.month = 'Para periodicidad bimestral selecciona un bimestre'
      }
      if (!isBimestral && monthNum >= 13) {
        newErrors.month = 'Para esta periodicidad selecciona un mes regular'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const fetchEligibleSales = useCallback(async () => {
    setIsLoadingSales(true)
    try {
      const { dateFrom, dateTo } = getDateRangeForPeriod(periodicity, month, year, dailyDate, week, quincena)

      // Se carga TODAS las ventas completas que NO han sido facturadas para el período
      const response = await salesService.getSales({
        per_page: 500,
        page: 1,
        invoice_status: 'not_invoiced',
        status: 'COMPLETED',
        date_from: dateFrom,
        date_to: dateTo,
        sucursal_id: sucursalId || undefined,
        bank_name: bankName !== 'all' ? bankName : undefined,
        payment_method_id: paymentMethod !== 'all' ? paymentMethod : undefined,
        sale_type: saleType !== 'all' ? saleType : undefined,
      })

      // Filtra sólo las ventas a Público en General
      const gpSales = response.data.filter(isGeneralPublic)
      setEligibleSales(gpSales)
      setSelectedIds(new Set(gpSales.map((s) => s.id)))
    } catch {
      toast.error('Error al cargar las ventas del período')
    } finally {
      setIsLoadingSales(false)
    }
  }, [periodicity, month, year, dailyDate, week, quincena, sucursalId, bankName, paymentMethod, saleType])

  const goToReview = async () => {
    if (!validateStep1()) return
    await fetchEligibleSales()
    setStep('review')
  }

  const goToConfirm = () => {
    if (selectedIds.size === 0) {
      toast.error('Debes seleccionar al menos una venta')
      return
    }
    setStep('confirm')
  }

  const toggleSale = (id: string | number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selectedIds.size === eligibleSales.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(eligibleSales.map((s) => s.id)))
    }
  }

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      let finalMonth = month
      let finalYear = year
      if (periodicity === '01' && dailyDate) {
        const [y, m] = dailyDate.split('-')
        finalYear = y
        finalMonth = m
      }

      const cfdi = await facturamaService.createGlobalInvoice(
        Array.from(selectedIds),
        periodicity,
        finalMonth,
        finalYear,
      )

      setGeneratedCfdiId(cfdi?.Id || cfdi?.id || null)
      setStep('success')
      toast.success('Factura global generada correctamente')
      queryClient.invalidateQueries({ queryKey: ['sales'] })
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al generar la factura global')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (!generatedCfdiId) return
    setDownloadingPdf(true)
    try {
      toast.info('Descargando PDF...')
      const base64 = await facturamaService.downloadInvoicePdf(generatedCfdiId)
      const link = document.createElement('a')
      link.href = `data:application/pdf;base64,${base64}`
      link.download = `Factura_Global_${generatedCfdiId}.pdf`
      link.click()
      toast.success('PDF descargado correctamente')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al descargar el PDF')
    } finally {
      setDownloadingPdf(false)
    }
  }

  const handleDownloadXml = async () => {
    if (!generatedCfdiId) return
    setDownloadingXml(true)
    try {
      toast.info('Descargando XML...')
      const base64 = await facturamaService.downloadInvoiceXml(generatedCfdiId)
      const link = document.createElement('a')
      link.href = `data:application/xml;base64,${base64}`
      link.download = `Factura_Global_${generatedCfdiId}.xml`
      link.click()
      toast.success('XML descargado correctamente')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al descargar el XML')
    } finally {
      setDownloadingXml(false)
    }
  }

  const selectedTotal = eligibleSales
    .filter((s) => selectedIds.has(s.id))
    .reduce((acc, s) => acc + s.total, 0)

  const periodicityLabel = PERIODICITY_OPTIONS.find((p) => p.value === periodicity)?.label ?? ''
  const monthLabel = MONTH_OPTIONS.find((m) => m.value === month)?.label ?? ''

  const currentStepIndex = STEPS.findIndex((s) => s.key === step)

  const isBimestral = periodicity === '05'
  const filteredMonths = MONTH_OPTIONS.filter((m) => m.bimestral === isBimestral)

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex max-h-[90vh] flex-col gap-0 overflow-y-auto p-0 sm:max-w-2xl"
        aria-describedby={undefined}
      >
        <DialogHeader className="sticky top-0 z-10 border-b border-border bg-popover px-6 pb-4 pt-6">
          <DialogTitle className="flex items-center gap-2.5 text-base font-semibold text-card-foreground">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            Facturación Global
          </DialogTitle>

          {/* Step indicator */}
          <div className="flex items-center gap-1 mt-3">
            {STEPS.map((s, idx) => (
              <div key={s.key} className="flex items-center gap-1">
                <div
                  className={cn(
                    'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all',
                    step === s.key
                      ? 'bg-primary text-white'
                      : idx < currentStepIndex
                        ? 'bg-primary/15 text-primary'
                        : 'bg-muted text-muted-foreground',
                  )}
                >
                  <span>{s.number}</span>
                  <span>{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <ChevronRight className="h-3.5 w-3.5 text-table-foreground/50" />
                )}
              </div>
            ))}
          </div>
        </DialogHeader>

        {/* ── STEP 1: PERÍODO ── */}
        {step === 'period' && (
          <div className="flex flex-col gap-5 px-6 py-5">
            {/* Info banner */}
            <div className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-3.5">
              <Info className="h-4 w-4 mt-0.5 text-blue-500 shrink-0" />
              <p className="text-xs text-blue-700 leading-relaxed">
                La facturación global solo aplica a ventas de <strong>Público en General</strong>{' '}
                (sin RFC o con RFC XAXX010101000) que estén completadas y sin facturar. El período
                debe corresponder al mes o bimestre del SAT.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Sucursal */}
              <div className="col-span-2 flex flex-col gap-1.5">
                <Label className="text-sm text-card-foreground">Sucursal</Label>
                <CatalogSelect
                    catalog="sucursales"
                    value={sucursalId}
                    onChange={(val) => setSucursalId(val || '')}
                    placeholder="Todas las sucursales"
                    className="bg-card text-card-foreground/70 border-border"
                  />
              </div>

              <div className="col-span-2 flex flex-col gap-1.5">
                <Label className="text-sm text-slate-700">Tipo de venta</Label>
                <Select value={saleType} onValueChange={(value) => setSaleType(value as typeof saleType)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los tipos de venta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos de venta</SelectItem>
                    <SelectItem value="recharge">Recarga</SelectItem>
                    <SelectItem value="service">Servicio</SelectItem>
                    <SelectItem value="common">Venta común</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Banco (Opcional) */}
              <div className="col-span-2 flex flex-col gap-1.5">
                <Label className="text-sm text-card-foreground">Banco de trazabilidad (Opcional)</Label>
                <Select value={bankName} onValueChange={setBankName}>
                  <SelectTrigger className="border-border bg-card text-muted-foreground">
                    <SelectValue placeholder="Todos los bancos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los bancos</SelectItem>
                    {banks.map((b) => (
                      <SelectItem key={b.id} value={b.name}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Metodo de Pago */}
              <div className="col-span-2 flex flex-col gap-1.5">
                <Label className="text-sm text-slate-700">Método de Pago</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los métodos de pago" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los métodos de pago</SelectItem>
                    {paymentMethods.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Periodicidad */}
              <div className="col-span-2 flex flex-col gap-1.5">
                <Label className="text-sm text-card-foreground">
                  Periodicidad <span className="text-card-foreground font-normal">(requerida por SAT)</span>
                </Label>
                <Select
                  value={periodicity}
                  onValueChange={(val) => {
                    setPeriodicity(val)
                    setErrors((prev) => ({ ...prev, month: undefined }))
                  }}
                >
                  <SelectTrigger className="border-border bg-card text-muted-foreground ">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODICITY_OPTIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Configuración específica por periodicidad */}
            {periodicity === '01' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 flex flex-col gap-1.5">
                  <Label className={cn('text-sm text-card-foreground', errors.dailyDate && 'text-red-600')}>
                    Fecha <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="date"
                    value={dailyDate}
                    onChange={(e) => {
                      setDailyDate(e.target.value)
                      setErrors((prev) => ({ ...prev, dailyDate: undefined }))
                    }}
                    className= {cn("border-border bg-card text-muted-foreground",errors.dailyDate && 'border-red-400 ring-red-400/20')}
                  />
                  {errors.dailyDate && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.dailyDate}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {/* Mes / Bimestre */}
                <div className="flex flex-col gap-1.5">
                  <Label className={cn('text-sm text-card-foreground', errors.month && 'text-red-600')}>
                    {isBimestral ? 'Bimestre' : 'Mes'} <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={month}
                    onValueChange={(val) => {
                      setMonth(val)
                      setErrors((prev) => ({ ...prev, month: undefined }))
                    }}
                  >
                    <SelectTrigger className={cn("border-border bg-card text-muted-foreground",errors.month && 'border-red-400 ring-red-400/20')}>
                      <SelectValue placeholder={isBimestral ? 'Selecciona bimestre' : 'Selecciona mes'} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredMonths.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.month && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.month}
                    </p>
                  )}
                </div>

                {/* Año */}
                <div className="flex flex-col gap-1.5">
                  <Label className={cn('text-sm text-card-foreground', errors.year && 'text-red-600')}>
                    Año <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={year}
                    onValueChange={(val) => {
                      setYear(val)
                      setErrors((prev) => ({ ...prev, year: undefined }))
                    }}
                  >
                    <SelectTrigger className={cn("border-border bg-card text-muted-foreground",errors.year && 'border-red-400 ring-red-400/20')}>
                      <SelectValue placeholder="Selecciona año" />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABLE_YEARS.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.year && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.year}
                    </p>
                  )}
                </div>

                {/* Semanal: Semana selector */}
                {periodicity === '02' && (
                  <div className="col-span-2 flex flex-col gap-1.5">
                    <Label className="text-sm text-card-foreground">Semana del mes</Label>
                    <Select value={week} onValueChange={setWeek}>
                      <SelectTrigger className="border-border bg-card text-muted-foreground">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Semana 1 (1 al 7)</SelectItem>
                        <SelectItem value="2">Semana 2 (8 al 14)</SelectItem>
                        <SelectItem value="3">Semana 3 (15 al 21)</SelectItem>
                        <SelectItem value="4">Semana 4 (22 al 28)</SelectItem>
                        <SelectItem value="5">Semana 5 (29 al fin de mes)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Quincenal: Quincena selector */}
                {periodicity === '03' && (
                  <div className="col-span-2 flex flex-col gap-1.5">
                    <Label className="text-sm text-card-foreground">Quincena del mes</Label>
                    <Select value={quincena} onValueChange={setQuincena}>
                      <SelectTrigger className="border-border bg-card text-muted-foreground">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Primera (1 al 15)</SelectItem>
                        <SelectItem value="2">Segunda (16 al fin de mes)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Date range preview */}
            {dateRange && (
              <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-card-foreground">
                <Calendar className="h-4 w-4 shrink-0 text-card-description" />
                <div className="flex flex-wrap items-center gap-2 text-sm text-card-description">
                <span className="font-semibold text-card-foreground">
                  {formatDateLabel(dateRange.dateFrom)}
                </span>

                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-card-description" />

                <span className="font-semibold text-card-foreground">
                  {formatDateLabel(dateRange.dateTo)}
                </span>
              </div>
                <Badge className="ml-auto bg-primary/10 text-primary border-0 text-xs shrink-0">
                  {periodicityLabel}
                </Badge>
              </div>
            )}

            <div className="flex justify-end pt-1">
             <Button
              onClick={goToReview}
              className="h-11 gap-2 bg-primary px-5 text-primary-foreground hover:bg-primary/90"
            >
              <Search className="h-4 w-4" />
              Buscar ventas
              <ChevronRight className="h-4 w-4" />
            </Button>
            </div>
          </div>
        )}


        {/* ── STEP 2: REVISIÓN ── */}
        {step === 'review' && (
          <div className="flex flex-col min-h-0">
            {/* Period summary bar */}
            <div className="flex items-center gap-3 border-b border-border bg-card px-6 py-3">
              <div className="flex items-center gap-1.5 text-xs text-card-foreground">
                <Calendar className="h-3.5 w-3.5 text-card-description" />
                <span className="font-medium">{monthLabel} {year}</span>
              </div>
              <span className="text-slate-300">·</span>
              <div className="flex items-center gap-1.5 text-xs text-card-foreground">
                <Receipt className="h-3.5 w-3.5 text-card-description" />
                <span>{periodicityLabel}</span>
              </div>
              {sucursalId && (
                <>
                  <span className="text-slate-300">·</span>
                  <div className="flex items-center gap-1.5 text-xs text-card-foreground">
                    <Building2 className="h-3.5 w-3.5 text-card-description" />
                    <span>Sucursal seleccionada</span>
                  </div>
                </>
              )}
              <div className="ml-auto">
                <button
                  onClick={() => setStep('period')}
                  className="text-xs text-primary hover:underline"
                >
                  Cambiar período
                </button>
              </div>
            </div>

            {isLoadingSales ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <p className="text-sm text-slate-500">Buscando ventas elegibles...</p>
              </div>
            ) : eligibleSales.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 px-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <AlertCircle className="h-6 w-6 text-slate-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">Sin ventas elegibles</p>
                  <p className="mt-1 text-sm text-slate-500">
                    No se encontraron ventas de Público en General sin facturar en este período.
                  </p>
                </div>
                <Button variant="outline" onClick={() => setStep('period')} className="mt-2">
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Cambiar período
                </Button>
              </div>
            ) : (
              <>
                {/* Live counter */}
                <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
                  <button
                    onClick={toggleAll}
                    className="flex items-center gap-2 text-sm font-medium text-card-foreground transition-colors hover:text-primary"
                  >
                    {selectedIds.size === eligibleSales.length ? (
                      <CheckSquare className="h-4 w-4 text-primary" />
                    ) : (
                      <Square className="h-4 w-4 text-slate-400" />
                    )}
                    {selectedIds.size === eligibleSales.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
                  </button>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-card-description">
  <span className="font-semibold text-card-foreground">{selectedIds.size}</span>
                      {' '}de {eligibleSales.length} ventas
                    </span>
                    <span className="rounded-full bg-primary/10 px-3 py-0.5 text-xs font-semibold text-primary">
                      Total: ${selectedTotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Sales list */}
                <div className="overflow-y-auto max-h-72">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 border-b border-border bg-table-header-bg text-table-header-foreground">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-table-header-foreground uppercase w-10"></th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-table-header-foreground uppercase">Ticket</th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-table-header-foreground uppercase">Fecha</th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-table-header-foreground uppercase">Sucursal</th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-table-header-foreground0 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
                      {eligibleSales.map((sale) => {
                        const isChecked = selectedIds.has(sale.id)
                        return (
                          <tr
                            key={sale.id}
                            onClick={() => toggleSale(sale.id)}
                            className={cn(
                              'cursor-pointer transition-colors',
                              isChecked  ? 'bg-primary/10 hover:bg-primary/15'
  : 'hover:bg-table-row-hover',
                            )}
                          >
                            <td className="px-4 py-2.5">
                              {isChecked ? (
                                <CheckSquare className="h-4 w-4 text-primary" />
                              ) : (
                                <Square className="h-4 w-4 text-slate-300" />
                              )}
                            </td>
                            <td className="px-3 py-2.5 font-mono text-xs font-medium text-table-foreground" title={sale.ticket_number || String(sale.id)}>
                              {formatShortTicket(sale.ticket_number || sale.id)}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-table-foreground">
                              {sale.date} {sale.time}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-table-foreground">
                              {(sale as any).branch || '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right font-semibold text-table-foreground">
                              ${sale.total.toFixed(2)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between border-t border-border bg-card px-6 py-4">
                  <Button variant="outline" onClick={() => setStep('period')}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Atrás
                  </Button>
                  <Button onClick={goToConfirm} disabled={selectedIds.size === 0}>
                    Continuar
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── STEP 3: CONFIRMACIÓN ── */}
        {step === 'confirm' && (
          <div className="flex flex-col gap-5 px-6 py-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 divide-y divide-slate-200">
              <div className="flex items-center justify-between px-5 py-3.5">
                <span className="text-sm text-slate-500">Ventas a facturar</span>
                <span className="font-semibold text-slate-900">{selectedIds.size} ventas</span>
              </div>
              <div className="flex items-center justify-between px-5 py-3.5">
                <span className="text-sm text-slate-500">Monto total</span>
                <span className="text-lg font-bold text-primary">${selectedTotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between px-5 py-3.5">
                <span className="text-sm text-slate-500">Período</span>
                <span className="font-medium text-slate-700">{monthLabel} {year}</span>
              </div>
              <div className="flex items-center justify-between px-5 py-3.5">
                <span className="text-sm text-slate-500">Periodicidad SAT</span>
                <Badge className="bg-primary/10 text-primary border-0">{periodicityLabel}</Badge>
              </div>
              <div className="flex items-center justify-between px-5 py-3.5">
                <span className="text-sm text-slate-500">Receptor</span>
                <span className="font-mono text-xs text-slate-700">XAXX010101000 · PÚBLICO EN GENERAL</span>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-white p-4">
  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />

  <p className="text-sm leading-6 text-primary">
    Esta acción generará un CFDI global en Facturama y marcará las ventas como facturadas.
    <strong> Esta operación no puede deshacerse.</strong>
  </p>
</div>

            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => setStep('review')} disabled={isSubmitting}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                Revisar ventas
              </Button>
              <Button onClick={handleConfirm} disabled={isSubmitting} className="gap-2">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Generar Factura Global
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 4: SUCCESS ── */}
        {step === 'success' && (
          <div className="flex flex-col gap-5 px-6 py-5">
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-slate-900">¡Factura Global Generada!</h3>
                <p className="mt-1 text-sm text-slate-500">
                  El proceso ha concluido exitosamente. El CFDI ha sido timbrado.
                </p>
                {generatedCfdiId && (
                  <p className="mt-3 text-xs font-mono text-slate-500 bg-slate-50 py-1.5 px-3 rounded-md inline-block border border-slate-200">
                    CFDI ID: {generatedCfdiId}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-2 px-8">
              <Button
                variant="outline"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf || !generatedCfdiId}
                className="gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
              >
                {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin text-red-500" /> : <FileText className="h-4 w-4 text-red-500" />}
                Descargar PDF
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadXml}
                disabled={downloadingXml || !generatedCfdiId}
                className="gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
              >
                {downloadingXml ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> : <FileText className="h-4 w-4 text-blue-500" />}
                Descargar XML
              </Button>
            </div>

            <div className="flex justify-end pt-4 mt-4 border-t border-slate-100">
              <Button onClick={() => { onSuccess(); onClose() }} className="min-w-[120px]">
                Finalizar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
